import React, { createContext, useState, useContext, useEffect } from 'react';
import { login as apiLogin } from '../services/api';

const AuthContext = createContext(null);

const AUTH_STORAGE_KEY = 'teetimetony_auth';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Initialize state from localStorage if available
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      try {
        const authData = JSON.parse(stored);
        return authData.isAuthenticated || false;
      } catch (e) {
        return false;
      }
    }
    return false;
  });

  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      try {
        const authData = JSON.parse(stored);
        return authData.user || null;
      } catch (e) {
        return null;
      }
    }
    return null;
  });

  // Save to localStorage whenever auth state changes
  useEffect(() => {
    if (isAuthenticated && user) {
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({
        isAuthenticated,
        user,
      }));
    } else {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    }
  }, [isAuthenticated, user]);

  const login = async (username, password) => {
    try {
      if (!username || !password) {
        throw new Error('Username and password are required');
      }

      const response = await apiLogin(username, password);
      
      // Response contains: Id, Username, IsAdmin, LastLoginDate
      setIsAuthenticated(true);
      setUser({
        id: response.Id,
        username: response.Username,
        isAdmin: response.IsAdmin,
        lastLoginDate: response.LastLoginDate,
      });
      return true;
    } catch (error) {
      console.error('Login error:', error);
      // Extract error message from API response if available
      const errorMessage = error.response?.data?.message || error.message || 'Login failed';
      throw new Error(errorMessage);
    }
  };

  const logout = () => {
    setIsAuthenticated(false);
    setUser(null);
    localStorage.removeItem(AUTH_STORAGE_KEY);
  };

  const value = {
    isAuthenticated,
    user,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

